import { withAlpha } from "@/lib/color";
import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_LABEL = {
    pending: { label: "In attesa", color: "rgb(var(--tc-e8ff00))" },
    active: { label: "Attivo", color: "rgb(var(--tc-2bb6ff))" },
    rejected: { label: "Rifiutato", color: "rgb(var(--tc-ff3b30))" },
};

const ROLE_LABEL = {
    admin: "Admin",
    presidente: "Presidente",
    guest: "Guest",
};

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [filter, setFilter] = useState("all");
    const [err, setErr] = useState("");
    // Reset password state
    const [resetTarget, setResetTarget] = useState(null);
    const [customPw, setCustomPw] = useState("");
    const [resetResult, setResetResult] = useState(null);
    const [resetting, setResetting] = useState(false);
    // Create user modal
    const [showCreate, setShowCreate] = useState(false);
    const [newU, setNewU] = useState({ name: "", email: "", password: "", role: "presidente", team_id: "" });
    const [creating, setCreating] = useState(false);
    const [createdInfo, setCreatedInfo] = useState(null);
    // Inline name edit
    const [editingNameId, setEditingNameId] = useState(null);
    const [editingNameValue, setEditingNameValue] = useState("");

    const load = useCallback(async () => {
        const [u, t] = await Promise.all([api.get("/admin/users"), api.get("/teams")]);
        setUsers(u.data);
        setTeams(t.data);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const patch = async (id, body) => {
        setErr("");
        try {
            await api.patch(`/admin/users/${id}`, body);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare definitivamente questo utente?")) return;
        try {
            await api.delete(`/admin/users/${id}`);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const openReset = (user) => {
        setResetTarget(user);
        setCustomPw("");
        setResetResult(null);
        setErr("");
    };

    const doReset = async () => {
        if (!resetTarget) return;
        setResetting(true);
        setErr("");
        try {
            const { data } = await api.post(`/admin/users/${resetTarget.id}/reset-password`, {
                new_password: customPw || null,
            });
            setResetResult(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setResetting(false);
        }
    };

    const closeReset = () => {
        setResetTarget(null);
        setCustomPw("");
        setResetResult(null);
    };

    const copyPw = () => {
        if (resetResult?.new_password) {
            navigator.clipboard.writeText(resetResult.new_password);
        }
    };

    const startEditName = (u) => {
        setEditingNameId(u.id);
        setEditingNameValue(u.name || "");
        setErr("");
    };
    const cancelEditName = () => {
        setEditingNameId(null);
        setEditingNameValue("");
    };
    const saveName = async (id) => {
        const nm = editingNameValue.trim();
        if (!nm || nm.length < 2) {
            setErr("Nome troppo corto");
            return;
        }
        await patch(id, { name: nm });
        setEditingNameId(null);
        setEditingNameValue("");
    };

    const openCreate = () => {
        setShowCreate(true);
        setNewU({ name: "", email: "", password: "", role: "presidente", team_id: "" });
        setCreatedInfo(null);
        setErr("");
    };
    const closeCreate = () => {
        setShowCreate(false);
        setNewU({ name: "", email: "", password: "", role: "presidente", team_id: "" });
        setCreatedInfo(null);
    };
    const submitCreate = async () => {
        setErr("");
        if (!newU.name.trim() || newU.name.trim().length < 2) {
            setErr("Nome obbligatorio (min 2 caratteri)");
            return;
        }
        if (!newU.email.trim()) { setErr("Email obbligatoria"); return; }
        if (!newU.password || newU.password.length < 6) {
            setErr("Password obbligatoria (min 6 caratteri)");
            return;
        }
        setCreating(true);
        try {
            const { data } = await api.post("/admin/users", {
                name: newU.name.trim(),
                email: newU.email.trim().toLowerCase(),
                password: newU.password,
                role: newU.role,
                team_id: newU.team_id || null,
                status: "active",
            });
            setCreatedInfo(data);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setCreating(false);
        }
    };
    const copyCreatedPw = () => {
        if (createdInfo?.created_password) navigator.clipboard.writeText(createdInfo.created_password);
    };

    const visible = users.filter((u) => filter === "all" || u.status === filter);

    return (
        <div data-testid="admin-users">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <h2 className="op-heading text-3xl">Utenti</h2>
                    <p className="op-label mt-2">Approva, promuovi, assegna squadre</p>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                    <button
                        type="button"
                        onClick={openCreate}
                        className="op-btn op-btn-primary text-sm"
                        data-testid="create-user-btn"
                    >
                        + Crea Presidente
                    </button>
                    {["all", "pending", "active", "rejected"].map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`op-btn ${filter === f ? "op-btn-primary" : ""}`}
                            data-testid={`users-filter-${f}`}
                        >
                            {f === "all" ? "Tutti" : STATUS_LABEL[f]?.label}
                        </button>
                    ))}
                </div>
            </div>

            {err && (
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-sm p-3 mb-4">
                    {err}
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="w-full border border-t22252a" data-testid="users-table">
                    <thead>
                        <tr className="bg-t1a1d24 op-label">
                            <th className="text-left p-3">Utente</th>
                            <th className="text-left p-3">Status</th>
                            <th className="text-left p-3">Ruolo</th>
                            <th className="text-left p-3">Squadra</th>
                            <th className="text-right p-3">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((u) => (
                            <tr
                                key={u.id}
                                className="border-t border-t22252a hover:bg-t15171c"
                                data-testid={`user-row-${u.id}`}
                            >
                                <td className="p-3">
                                    {editingNameId === u.id ? (
                                        <div className="flex items-center gap-1">
                                            <input
                                                type="text"
                                                value={editingNameValue}
                                                onChange={(e) => setEditingNameValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") saveName(u.id);
                                                    else if (e.key === "Escape") cancelEditName();
                                                }}
                                                autoFocus
                                                className="op-input py-1 px-2 text-sm w-40"
                                                data-testid={`user-name-input-${u.id}`}
                                            />
                                            <button
                                                onClick={() => saveName(u.id)}
                                                className="text-t10b981 hover:text-tffffff px-1 text-lg"
                                                title="Salva (Invio)"
                                            >✓</button>
                                            <button
                                                onClick={cancelEditName}
                                                className="text-tff3b30 hover:text-tffffff px-1 text-lg"
                                                title="Annulla (Esc)"
                                            >✕</button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => startEditName(u)}
                                            className="text-left text-tffffff font-medium hover:text-tffc93c transition"
                                            title="Clicca per modificare il nome"
                                            data-testid={`user-name-${u.id}`}
                                        >
                                            {u.name} <span className="op-mono text-[10px] text-t5c6370 ml-1">✎</span>
                                        </button>
                                    )}
                                    <div className="op-mono text-xs text-t8f95a3">{u.email}</div>
                                </td>
                                <td className="p-3">
                                    <span
                                        className="op-label px-2 py-1 border"
                                        style={{
                                            color: STATUS_LABEL[u.status]?.color || "rgb(var(--tc-ffffff))",
                                            borderColor: withAlpha(STATUS_LABEL[u.status]?.color || "rgb(var(--tc-ffffff))", "55"),
                                        }}
                                    >
                                        {STATUS_LABEL[u.status]?.label || u.status}
                                    </span>
                                </td>
                                <td className="p-3">
                                    <select
                                        value={u.role}
                                        onChange={(e) => patch(u.id, { role: e.target.value })}
                                        className="op-input py-1.5"
                                        data-testid={`user-role-select-${u.id}`}
                                    >
                                        {Object.entries(ROLE_LABEL).map(([k, v]) => (
                                            <option key={k} value={k}>
                                                {v}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td className="p-3">
                                    <select
                                        value={u.team_id || ""}
                                        onChange={(e) => patch(u.id, { team_id: e.target.value })}
                                        className="op-input py-1.5"
                                        data-testid={`user-team-select-${u.id}`}
                                    >
                                        <option value="">— Nessuna —</option>
                                        {teams.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td className="p-3 text-right whitespace-nowrap">
                                    {u.status === "pending" && (
                                        <>
                                            <button
                                                onClick={() => patch(u.id, { status: "active" })}
                                                className="op-btn op-btn-primary py-1.5 px-3 text-xs mr-2"
                                                data-testid={`user-approve-btn-${u.id}`}
                                            >
                                                Approva
                                            </button>
                                            <button
                                                onClick={() => patch(u.id, { status: "rejected" })}
                                                className="op-btn py-1.5 px-3 text-xs mr-2"
                                                data-testid={`user-reject-btn-${u.id}`}
                                            >
                                                Rifiuta
                                            </button>
                                        </>
                                    )}
                                    {u.status === "active" && (
                                        <button
                                            onClick={() => patch(u.id, { status: "pending" })}
                                            className="op-btn py-1.5 px-3 text-xs mr-2"
                                            data-testid={`user-suspend-btn-${u.id}`}
                                        >
                                            Sospendi
                                        </button>
                                    )}
                                    {u.status === "rejected" && (
                                        <button
                                            onClick={() => patch(u.id, { status: "active" })}
                                            className="op-btn op-btn-primary py-1.5 px-3 text-xs mr-2"
                                            data-testid={`user-reactivate-btn-${u.id}`}
                                        >
                                            Riattiva
                                        </button>
                                    )}
                                    <button
                                        onClick={() => openReset(u)}
                                        className="op-btn op-btn-ghost py-1.5 px-3 text-xs text-tffc93c hover:text-tffc93c mr-2"
                                        data-testid={`user-reset-pw-btn-${u.id}`}
                                        title="Resetta password (genera o imposta)"
                                    >
                                        Reset PW
                                    </button>
                                    <button
                                        onClick={() => remove(u.id)}
                                        className="op-btn op-btn-ghost py-1.5 px-3 text-xs text-tff3b30 hover:text-tff3b30"
                                        data-testid={`user-delete-btn-${u.id}`}
                                    >
                                        Elimina
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {visible.length === 0 && (
                            <tr>
                                <td colSpan={5} className="p-8 text-center op-label">
                                    Nessun utente.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal Crea Presidente */}
            {showCreate && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
                    data-testid="create-user-modal"
                    onClick={closeCreate}
                >
                    <div className="op-card max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            onClick={closeCreate}
                            className="absolute top-3 right-4 text-2xl text-t8f95a3 hover:text-tffffff"
                            aria-label="Chiudi"
                        >×</button>
                        <div className="op-label mb-2" style={{ color: "rgb(var(--tc-2bb6ff))" }}>// CREA PRESIDENTE</div>

                        {!createdInfo ? (
                            <>
                                <h3 className="op-heading text-xl text-tffffff mb-4">Nuovo utente</h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="op-label block mb-1 text-[10px]">Nome presidente</label>
                                        <input
                                            type="text"
                                            value={newU.name}
                                            onChange={(e) => setNewU({ ...newU, name: e.target.value })}
                                            placeholder="Es. Peppe Russiola"
                                            className="op-input w-full text-sm"
                                            data-testid="create-name-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="op-label block mb-1 text-[10px]">Email</label>
                                        <input
                                            type="email"
                                            value={newU.email}
                                            onChange={(e) => setNewU({ ...newU, email: e.target.value })}
                                            placeholder="email@example.com"
                                            className="op-input w-full text-sm"
                                            data-testid="create-email-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="op-label block mb-1 text-[10px]">Password (visibile)</label>
                                        <input
                                            type="text"
                                            value={newU.password}
                                            onChange={(e) => setNewU({ ...newU, password: e.target.value })}
                                            placeholder="min 6 caratteri"
                                            className="op-input w-full op-mono text-sm"
                                            data-testid="create-pw-input"
                                        />
                                        {newU.password && newU.password.length < 6 && (
                                            <div className="op-mono text-[10px] text-tff3b30 mt-1">Min 6 caratteri</div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="op-label block mb-1 text-[10px]">Ruolo</label>
                                        <select
                                            value={newU.role}
                                            onChange={(e) => setNewU({ ...newU, role: e.target.value })}
                                            className="op-input w-full text-sm"
                                            data-testid="create-role-select"
                                        >
                                            {Object.entries(ROLE_LABEL).map(([k, v]) => (
                                                <option key={k} value={k}>{v}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="op-label block mb-1 text-[10px]">Squadra (opzionale)</label>
                                        <select
                                            value={newU.team_id}
                                            onChange={(e) => setNewU({ ...newU, team_id: e.target.value })}
                                            className="op-input w-full text-sm"
                                            data-testid="create-team-select"
                                        >
                                            <option value="">(nessuna)</option>
                                            {teams.map((t) => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="flex gap-2 mt-5 justify-end">
                                    <button type="button" onClick={closeCreate} className="op-btn op-btn-ghost text-sm">Annulla</button>
                                    <button
                                        type="button"
                                        onClick={submitCreate}
                                        disabled={creating}
                                        className="op-btn op-btn-primary text-sm"
                                        data-testid="create-submit-btn"
                                    >
                                        {creating ? "Creazione…" : "Crea utente"}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="border border-t10b981 bg-t10b981/10 p-4 mb-3 rounded-sm">
                                    <div className="op-label text-[10px] mb-1" style={{ color: "rgb(var(--tc-10b981))" }}>
                                        ✅ UTENTE CREATO
                                    </div>
                                    <div className="text-tffffff text-sm mt-2">{createdInfo.name}</div>
                                    <div className="op-mono text-xs text-t8f95a3">{createdInfo.email}</div>
                                    <div className="mt-3">
                                        <div className="op-label text-[10px] mb-1">Password (UNICA visualizzazione)</div>
                                        <div className="op-mono text-2xl text-tffffff font-bold break-all select-all"
                                             data-testid="created-pw-result">
                                            {createdInfo.created_password}
                                        </div>
                                    </div>
                                </div>
                                <div className="op-mono text-[11px] text-t8f95a3 mb-3">
                                    Copia password e dalla al presidente. Dopo la chiusura non sarà più visibile.
                                </div>
                                <div className="flex gap-2 justify-end">
                                    <button type="button" onClick={copyCreatedPw} className="op-btn op-btn-ghost text-sm" data-testid="created-pw-copy-btn">📋 Copia</button>
                                    <button type="button" onClick={closeCreate} className="op-btn op-btn-primary text-sm">Chiudi</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modal Reset Password */}
            {resetTarget && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
                    data-testid="reset-pw-modal"
                    onClick={closeReset}
                >
                    <div
                        className="op-card max-w-md w-full p-6 relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={closeReset}
                            className="absolute top-3 right-4 text-2xl text-t8f95a3 hover:text-tffffff"
                            aria-label="Chiudi"
                        >×</button>
                        <div className="op-label mb-2" style={{ color: "rgb(var(--tc-ffc93c))" }}>// RESET PASSWORD</div>
                        <h3 className="op-heading text-xl text-tffffff">{resetTarget.name}</h3>
                        <div className="op-mono text-xs text-t8f95a3 mb-4">{resetTarget.email}</div>

                        {!resetResult ? (
                            <>
                                <label className="op-label block mb-1.5 text-[10px]">
                                    Password personalizzata (lascia vuoto per generarla casuale)
                                </label>
                                <input
                                    type="text"
                                    value={customPw}
                                    onChange={(e) => setCustomPw(e.target.value)}
                                    placeholder="Auto-generate"
                                    className="op-input w-full op-mono text-sm"
                                    minLength={6}
                                    data-testid="reset-pw-custom-input"
                                />
                                {customPw.length > 0 && customPw.length < 6 && (
                                    <div className="op-mono text-[10px] text-tff3b30 mt-1">
                                        Minimo 6 caratteri
                                    </div>
                                )}
                                <div className="flex gap-2 mt-5 justify-end">
                                    <button
                                        type="button"
                                        onClick={closeReset}
                                        className="op-btn op-btn-ghost text-sm"
                                    >Annulla</button>
                                    <button
                                        type="button"
                                        onClick={doReset}
                                        disabled={resetting || (customPw.length > 0 && customPw.length < 6)}
                                        className="op-btn op-btn-primary text-sm"
                                        data-testid="reset-pw-confirm-btn"
                                    >
                                        {resetting ? "Reset…" : (customPw ? "Imposta" : "Genera & Reset")}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="border border-t10b981 bg-t10b981/10 p-4 mb-3 rounded-sm">
                                    <div className="op-label text-[10px] mb-1" style={{ color: "rgb(var(--tc-10b981))" }}>
                                        ✅ NUOVA PASSWORD (mostra UNA SOLA VOLTA)
                                    </div>
                                    <div className="op-mono text-2xl text-tffffff font-bold break-all select-all"
                                         data-testid="reset-pw-result">
                                        {resetResult.new_password}
                                    </div>
                                </div>
                                <div className="op-mono text-[11px] text-t8f95a3 mb-3">
                                    Copia questa password e dalla al presidente. Dopo la chiusura non sarà più visibile.
                                </div>
                                <div className="flex gap-2 justify-end">
                                    <button
                                        type="button"
                                        onClick={copyPw}
                                        className="op-btn op-btn-ghost text-sm"
                                        data-testid="reset-pw-copy-btn"
                                    >📋 Copia</button>
                                    <button
                                        type="button"
                                        onClick={closeReset}
                                        className="op-btn op-btn-primary text-sm"
                                    >Chiudi</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
